import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Access is enforced by the server on every assistant request; these controls
// only record the administrator's decision. AI access never adds permissions.

const lastChange = (data) => data?.updated_at
  ? `Last changed ${new Date(data.updated_at).toLocaleString()}${data.updated_by_name ? ` by ${data.updated_by_name}` : ""}.`
  : "Not changed yet (off by default).";

const ServiceNotice = ({ data }) => data && data.service_enabled === false
  ? <p className="text-xs text-amber-700 mt-2">The assistant is switched off on this server (DFA_ASSISTANT_ENABLED). Settings are saved but take effect only when it is switched on.</p>
  : null;

const AccessToggle = ({ id, label, checked, onChange, disabled }) => <label htmlFor={id} className="flex items-center gap-3 text-sm text-[#1B2C5A] cursor-pointer">
  <input id={id} type="checkbox" className="w-4 h-4 accent-[#1B2C5A]" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
  {label}
</label>;

const useAccess = (url) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const current = url ? (data?.parents_enabled ?? data?.enabled) : false;
  useEffect(() => {
    if (!url) return;
    api.get(url).then(({ data: result }) => { setData(result); setDraft(Boolean(result.parents_enabled ?? result.enabled)); setError(""); })
      .catch((err) => setError(formatApiError(err)));
  }, [url]);
  const save = async () => {
    setSaving(true);
    try {
      const { data: result } = await api.put(url, { enabled: draft });
      setData(result); setDraft(Boolean(result.parents_enabled ?? result.enabled));
      toast.success(draft ? "DFA Assistant access enabled" : "DFA Assistant access disabled");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return { data, draft, setDraft, saving, error, save, dirty: Boolean(data) && draft !== Boolean(current) };
};

export const ParentAssistantSettingsCard = () => {
  const access = useAccess("/assistant/admin/settings");
  return <Card className="p-6 border-[#E2E8F0] lg:col-span-2" data-testid="assistant-parent-settings">
    <div className="overline text-[#9DCD3F]">Administration</div>
    <h3 className="font-display text-2xl text-[#1B2C5A] mt-2 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#9DCD3F]" /> DFA Virtual Assistant Settings</h3>
    <p className="text-sm text-[#475569] mt-2">Controls whether parents can use the assistant for fees, school items, payments and receipts. Parents only ever see their own children's records.
      Staff access is granted individually under <strong>Staff → profile → Login &amp; Access</strong>.</p>
    {access.error ? <p className="text-sm text-red-700 mt-4">{access.error}</p> : !access.data
      ? <div className="mt-4 text-sm text-[#475569] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      : <div className="mt-4 rounded-lg border border-[#E2E8F0] bg-[#FDFBF7] p-4">
        <div className="overline text-[#9DCD3F] mb-2">Parents</div>
        <AccessToggle id="assistant-parents-enabled" label="Enable Virtual Assistant for Parents" checked={access.draft} onChange={access.setDraft} disabled={access.saving} />
        <p className="text-xs text-[#475569] mt-2">{lastChange(access.data)}</p>
        <ServiceNotice data={access.data} />
        <Button className="mt-3 bg-[#1B2C5A] hover:bg-[#14224A] text-white" disabled={!access.dirty || access.saving} onClick={access.save} data-testid="assistant-parents-save">
          {access.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Save</Button>
      </div>}
  </Card>;
};

export const StaffAssistantAccess = ({ staffId }) => {
  const access = useAccess(staffId ? `/assistant/admin/staff/${staffId}/access` : null);
  if (access.error) return <p className="text-sm text-red-700 mt-5">{access.error}</p>;
  if (!access.data) return null;
  if (!access.data.has_login) return <p className="text-xs text-[#475569] mt-5" data-testid="assistant-staff-access">Create a login for this staff member before enabling the DFA Virtual Assistant.</p>;
  return <div className="mt-5 rounded-lg border border-[#E2E8F0] bg-[#FDFBF7] p-4" data-testid="assistant-staff-access">
    <div className="overline text-[#9DCD3F] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> DFA Virtual Assistant</div>
    <div className="mt-2"><AccessToggle id={`assistant-staff-${staffId}`} label="Allow this staff member to use the Virtual Assistant" checked={access.draft} onChange={access.setDraft} disabled={access.saving} /></div>
    <p className="text-xs text-[#475569] mt-2">The assistant can only do what this account's roles and permissions already allow. Staff tasks are being added in a later phase. {lastChange(access.data)}</p>
    <ServiceNotice data={access.data} />
    <Button size="sm" className="mt-3 bg-[#1B2C5A] hover:bg-[#14224A] text-white" disabled={!access.dirty || access.saving} onClick={access.save} data-testid="assistant-staff-save">
      {access.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Save</Button>
  </div>;
};
